import {
  Autocomplete as MuiAutocomplete,
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps as MuiAutocompleteProps,
  TextField,
  TextFieldProps,
  AutocompleteValue,
} from '@mui/material'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'

export type Option = {
  label: string
  value: string
  additional_fields?: Record<string, unknown>
  description?: string
}

export type DropdownProps<
  T extends Option,
  P extends FieldValues,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'> &
  Omit<TextFieldProps, 'error' | 'onChange' | 'required'> &
  UseControllerProps<P> & {
    showOptionDescription?: boolean
    customOnChange?: (value: string | null) => void
  }

export const Dropdown = <
  T extends Option,
  P extends FieldValues,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  control,
  name,
  defaultValue,
  rules,
  label,
  placeholder,
  options,
  slotProps,
  onBlur,
  customOnChange,
  multiple,
  helperText,
  showOptionDescription = false,
  onChange: onChangeProp,
  ...rest
}: DropdownProps<T, P, Multiple, DisableClearable, FreeSolo>) => {
  const {
    field: { ref, onBlur: onFieldBlur, onChange: onFieldChange, value, ...field },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  })

  function isOption(param: unknown): param is Option {
    return Boolean((param as Option)?.value !== undefined && (param as Option)?.label !== undefined)
  }

  function getOptionLabel(option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) {
    return isOption(option) ? option.label : option.toString()
  }

  function getOptionValue(option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) {
    return isOption(option) ? option.value.toString() : option.toString()
  }
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const selectedOption: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo> = multiple
    ? options.filter((option) => (value as string[])?.includes(option.value?.toString()))
    : options.find((option) => option.value.toString() === value?.toString()) || (rest.freeSolo ? (value as any) : null)

  return (
    <MuiAutocomplete
      data-testid="dropdown-autocomplete"
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => getOptionValue(option) === getOptionValue(value)}
      renderOption={
        showOptionDescription
          ? (props, option) => (
              <li {...props} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{option.label}</span>
                {option.description && (
                  <span
                    style={{
                      marginLeft: '8px',
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      padding: '2px 4px',
                      borderRadius: '4px',
                    }}
                  >
                    {option.description}
                  </span>
                )}
              </li>
            )
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder={placeholder}
          error={!!error}
          helperText={error?.message || helperText}
          inputRef={ref}
          slotProps={{
            input: {
              ...(slotProps?.input || {}),
              ...params.InputProps,
            },
          }}
        />
      )}
      onChange={(event, value, reason, details) => {
        const optionValue = multiple
          ? (value as T[]).map((option) => (isOption(option) ? option.value.toString() : option))
          : isOption(value)
            ? value.value.toString()
            : options.length > 0
              ? ''
              : value
        // Always update RHF field first; caller onChange must not replace this handler
        onFieldChange(optionValue)

        if (customOnChange) {
          const stringValue = isOption(value) ? value.value.toString() : typeof value === 'string' ? value : null
          customOnChange(stringValue)
        }

        onChangeProp?.(event, value, reason, details)
      }}
      fullWidth
      options={options}
      value={selectedOption}
      onBlur={(event) => {
        onFieldBlur()

        if (onBlur) {
          onBlur(event)
        }
      }}
      multiple={multiple}
      {...field}
      {...rest}
    />
  )
}
